/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Ref } from 'preact/hooks';
import { useUser } from '../../hooks/UNSAFE_useUser';
import { useDatatip } from './useDatatip_FORK';
import { calculateOffset } from '../../hooks/PRIVATE_useDatatip';
import type { GanttRowLayoutObj } from '../utils/dataLayoutUtils';
import type { Gantt as GanttProps, GanttRow, GanttTask } from '../Gantt.types';

type UseGanttRowLabelDatatipOptions<
  K1,
  K2,
  D1 extends GanttRow<K1, D1, D2>,
  D2 extends GanttTask<K2>
> = {
  targetRef: Ref<HTMLElement>;
  rowData: GanttProps<K2, D1, D2>['rows'];
  hoveredRowObj?: GanttRowLayoutObj<K1, K2>;
  focusedRowObj?: GanttRowLayoutObj<K1, K2>;
  currentRowLabelInteraction: 'hover' | 'focus' | 'none';
  rowAxisRegion: { x: number; y: number; w: number; h: number };
};

/**
 * Hook that handles row label datatip.
 */
const useGanttRowLabelDatatip = <
  K1,
  K2,
  D1 extends GanttRow<K1, D1, D2>,
  D2 extends GanttTask<K2>
>({
  targetRef,
  rowData,
  hoveredRowObj,
  focusedRowObj,
  currentRowLabelInteraction,
  rowAxisRegion
}: UseGanttRowLabelDatatipOptions<K1, K2, D1, D2>) => {
  const { direction } = useUser();
  const isRTL = direction === 'rtl';

  const config = {
    hover: {
      rowObj: hoveredRowObj,
      anchor: 'pointer' as const
    },
    focus: {
      rowObj: focusedRowObj,
      anchor: 'element' as const
    },
    none: {
      rowObj: undefined,
      anchor: 'pointer' as const
    }
  };

  const { anchor, rowObj: activeRowObj } = config[currentRowLabelInteraction];

  const content = activeRowObj ? rowData[activeRowObj.index].accessibleLabel : undefined;

  const elementBounds =
    activeRowObj && anchor === 'element'
      ? {
          x: 0,
          y: activeRowObj.y + rowAxisRegion.y,
          width: rowAxisRegion.w,
          height: activeRowObj.height
        }
      : undefined;

  return useDatatip({
    content,
    anchor,
    placement: 'top-start',
    offset: calculateOffset(isRTL, rowAxisRegion.w, elementBounds),
    targetFocusRef: targetRef
  });
};

export { useGanttRowLabelDatatip };
